/**
 * Cart drawer upsell.
 *
 * Adds a single accessory straight from the drawer, then hands the refreshed
 * cart back to the theme so the drawer re-renders itself. The markup (and the
 * chair-to-accessory-set matching behind it) is produced by
 * snippets/cart-upsell.liquid.
 *
 * The element is re-created every time the drawer re-renders through the
 * Section Rendering API, so all state lives in the DOM and `connectedCallback`
 * is free to run repeatedly. The impression event is deduped per cart state
 * via a module-level key so a re-render doesn't inflate the funnel.
 */

import { track } from '@theme/ga4-track';

/** Impression keys already reported, so drawer re-renders don't double-count. */
const reportedImpressions = new Set();

class CartUpsell extends HTMLElement {
  /** Removes the delegated listeners and the observer on disconnect. */
  #teardown = null;

  connectedCallback() {
    // Listeners are DELEGATED from this element, never bound per button.
    // Cart updates re-render the drawer through morph, which patches child
    // nodes in place without re-running connectedCallback — listeners bound
    // directly to the buttons here would go stale after the first update.
    // Delegation survives both morph outcomes: element patched (listeners
    // persist, new children delegate through them) and element recreated
    // (connectedCallback runs again).
    const controller = new AbortController();
    const { signal } = controller;

    this.addEventListener(
      'click',
      (event) => {
        const target = /** @type {Element} */ (event.target);
        const button = target && target.closest && target.closest('[data-role="add"]');
        if (!button) return;
        const item = button.closest('.cart-upsell__item');
        if (item) this.add(item, button);
      },
      { signal }
    );

    this.addEventListener(
      'change',
      (event) => {
        const target = /** @type {Element} */ (event.target);
        const control = target && target.closest && target.closest('select[data-role="variant"]');
        if (!control) return;
        const item = control.closest('.cart-upsell__item');
        if (item) this.selectVariant(item);
      },
      { signal }
    );

    // Morph can swap the offered products without recreating this element, so
    // impressions are re-checked on subtree changes; reportImpression dedupes
    // by offer-set key, so unchanged content costs nothing. Attributes are
    // watched too: the same product list can re-render under a different
    // chair context (data-chair / data-accessory-source change, ids don't),
    // which is a distinct impression key with no childList mutation.
    const observer = new MutationObserver(() => this.reportImpression());
    observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-chair', 'data-accessory-source'],
    });

    this.#teardown = () => {
      controller.abort();
      observer.disconnect();
    };

    this.reportImpression();
  }

  disconnectedCallback() {
    if (this.#teardown) this.#teardown();
    this.#teardown = null;
  }

  /** Looked up per use — morph may have replaced the node since last time. */
  get errorEl() {
    return this.querySelector('[data-role="error"]');
  }

  /**
   * The current choice, normalised across the two shapes the markup uses: a
   * <select> for multi-variant products, a hidden input for single-variant
   * ones. Returns the variant id plus whichever element carries the price and
   * image data for it.
   */
  static selection(item) {
    const control = item.querySelector('[data-role="variant"]');
    if (!control) return null;

    if (control.tagName === 'SELECT') {
      const option = control.options[control.selectedIndex];
      return { id: control.value, data: option ? option.dataset : {} };
    }
    return { id: control.value, data: control.dataset };
  }

  /** Swaps the card's price and thumbnail to match the chosen colour. */
  selectVariant(item) {
    const selection = CartUpsell.selection(item);
    if (!selection) return;

    const price = item.querySelector('[data-role="price"]');
    if (price && selection.data.price) price.textContent = selection.data.price;

    const image = item.querySelector('[data-role="image"]');
    if (image && selection.data.image) {
      image.src = selection.data.image;
      // The Liquid-rendered srcset points at the first variant's image, so it
      // would otherwise win over the src we just set.
      image.removeAttribute('srcset');
    }
  }

  /** Fires once per distinct set of offered products, not once per re-render. */
  reportImpression() {
    const items = Array.from(this.querySelectorAll('.cart-upsell__item'));
    const ids = items.map((item) => item.dataset.productId).join(',');
    const key = `${this.dataset.chair || ''}|${ids}`;
    if (!ids || reportedImpressions.has(key)) return;
    reportedImpressions.add(key);

    const chairCount = items.filter((item) => item.dataset.accessorySource === 'chair').length;

    track(this, 'cart_upsell_shown', {
      source: 'cart-upsell',
      chair: this.dataset.chair || null,
      accessory_set: this.dataset.set || null,
      item_count: items.length,
      // Split so the funnel can tell whether chair-matched accessories pull
      // their weight against the universal ones sharing the same slots.
      chair_item_count: chairCount,
      universal_item_count: items.length - chairCount,
    });
  }

  /**
   * Adds the selected variant, then dispatches `cart:update` with the FULL
   * cart as the resource. /cart/add.js only returns the newly-added lines, and
   * the drawer renders from `resource` — passing the add response alone makes
   * it show its empty state. This mirrors byob-builder.js and the theme's own
   * add-to-cart flow.
   */
  async add(item, button) {
    const selection = CartUpsell.selection(item);
    const variantId = selection && selection.id;
    if (!variantId) return;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    button.disabled = true;
    if (this.errorEl) this.errorEl.hidden = true;

    try {
      const addUrl =
        (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: { _cart_upsell: 'true' } }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const json = await response.json();

      if (!response.ok || json.status) {
        throw new Error(json.description || json.message || 'Could not add that to your cart.');
      }

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      let cart = null;
      try {
        const cartResponse = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
        if (cartResponse.ok) cart = await cartResponse.json();
      } catch (e) {
        /* fall back to the add response below */
      }

      const addedLine = json.items ? json.items[0] : null;
      const value = addedLine ? addedLine.final_line_price / 100 : null;
      const currency = this.dataset.currency || 'AUD';

      button.classList.add('is-added');

      track(this, 'cart_upsell_add_to_cart', {
        source: 'cart-upsell',
        chair: this.dataset.chair || null,
        accessory_set: this.dataset.set || null,
        accessory_source: item.dataset.accessorySource || null,
        product_title: button.dataset.productTitle || null,
        variant_id: String(variantId),
        value: value,
        currency: currency,
      });

      // GA4-standard add_to_cart too, so ad pixels and GA4 ecommerce count this
      // add — it bypasses the theme's own add-to-cart event entirely.
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            currency: currency,
            value: value,
            items: [{ item_id: String(variantId), item_name: button.dataset.productTitle, quantity: 1 }],
          },
        });
      } catch (e) {
        /* no dataLayer */
      }

      this.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart || json,
            sourceId: 'cart-upsell',
            data: {
              source: 'cart-upsell',
              itemCount: cart ? cart.item_count : 1,
              sections: json.sections,
            },
          },
        })
      );
    } catch (error) {
      if (this.errorEl) {
        this.errorEl.textContent = error.message;
        this.errorEl.hidden = false;
      }
      button.disabled = false;
      button.classList.remove('is-added');
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
